import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Check, Loader2, QrCode, Clock, ShieldCheck, PartyPopper, CreditCard, Zap, Tag, X } from "lucide-react";
import { PromoBanner } from "@/components/PromoBanner";
import { brl, PAYMENT_STATES } from "@/lib/money";

export function PixCheckout({ open, onOpenChange, onActivated, plan, period = "monthly", promotions = [] }) {
  const [pay, setPay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [autoUnavailable, setAutoUnavailable] = useState("");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [showPix, setShowPix] = useState(false);
  const planId = plan?.id || "starter";
  const body = { plan_id: planId, period, coupon: applied || null };

  const loadQuote = async (c) => {
    setQuoteErr("");
    try { const { data } = await api.post("/billing/quote", { plan_id: planId, period, coupon: c || null }); setQuote(data); return true; }
    catch (e) { setQuoteErr(e.response?.data?.detail || "Não foi possível calcular o valor."); if (c) setQuote(null); return false; }
  };

  useEffect(() => {
    if (!open) return;
    setPay(null); setShowPix(false); setAutoUnavailable(""); setApplied(""); setCoupon(""); setQuote(null);
    loadQuote("");
    api.get("/billing/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [open, planId, period]); // eslint-disable-line

  useEffect(() => {
    if (!open || status?.plan_status === "active") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/billing/status");
        setStatus(data);
        if (data.plan_status === "active") { toast.success(`Plano ${data.plan_name} ativado! 🎉`); onActivated?.(); }
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(t);
  }, [open, status?.plan_status, onActivated]);

  const applyCoupon = async () => {
    const c = coupon.trim().toUpperCase();
    if (!c) return;
    const ok = await loadQuote(c);
    if (ok) { setApplied(c); setPay(null); setShowPix(false); toast.success("Cupom aplicado!"); } else { await loadQuote(""); }
  };
  const removeCoupon = async () => { setApplied(""); setCoupon(""); setPay(null); setShowPix(false); await loadQuote(""); };

  const payAuto = async () => {
    setRedirecting(true);
    try { const { data } = await api.post("/billing/checkout", body); window.location.assign(data.checkout_url); }
    catch (e) { setAutoUnavailable(e.response?.data?.detail || "Checkout automático indisponível no momento."); setRedirecting(false); }
  };
  const openPix = async () => {
    setLoading(true); setShowPix(true);
    try { const { data } = await api.post("/billing/pix", body); setPay(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Não foi possível gerar o Pix."); setShowPix(false); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(pay.brcode); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Código Pix copiado!"); }
    catch { toast.error("Copie manualmente o código abaixo."); }
  };
  const reportPaid = async () => {
    try { await api.post(`/billing/pix/${pay.id}/paid`); setPay((p) => ({ ...p, status: "aguardando_confirmacao" })); toast.success("Recebido! Vamos confirmar seu Pix em breve."); }
    catch { toast.error("Erro ao informar pagamento."); }
  };

  const waiting = pay?.status === "aguardando_confirmacao";
  const active = status?.plan_status === "active";
  const pendingAuto = status?.pending_payment?.provider === "infinitepay" && status.pending_payment.status === "pendente";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto border-slate-800 bg-slate-950 text-slate-100" data-testid="pix-checkout">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="checkout-title">Assinar {plan?.name || "Starter"} · {period === "annual" ? "anual" : "mensal"}</DialogTitle>
          <DialogDescription className="text-slate-400">Pague com Pix ou cartão. Seu plano é liberado somente após a confirmação real do pagamento.</DialogDescription>
        </DialogHeader>

        {active ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="pix-active">
            <PartyPopper className="h-10 w-10 text-emerald-400" />
            <p className="font-display text-lg font-semibold text-white">Plano {status.plan_name} ativo!</p>
            <p className="text-sm text-slate-400">Válido até {status.plan_expires_at ? new Date(status.plan_expires_at).toLocaleDateString("pt-BR") : "—"}.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <PromoBanner promotions={promotions} planId={planId} compact />

            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3" data-testid="coupon-box">
              <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Tag className="h-3.5 w-3.5" /> Cupom de desconto</p>
              {applied ? (
                <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200" data-testid="coupon-applied">
                  <span>Cupom <b className="font-mono-flow">{applied}</b> aplicado</span>
                  <button type="button" onClick={removeCoupon} className="text-emerald-300 hover:text-white" data-testid="coupon-remove-btn"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Digite o código" className="border-slate-700 bg-slate-950 font-mono-flow uppercase" data-testid="coupon-input" onKeyDown={(e) => e.key === "Enter" && applyCoupon()} />
                  <Button type="button" variant="outline" onClick={applyCoupon} disabled={!coupon.trim()} className="border-slate-700 bg-slate-900/50 text-cyan-300" data-testid="coupon-apply-btn">Aplicar</Button>
                </div>
              )}
              {quoteErr && <p className="mt-2 text-xs text-red-300" data-testid="coupon-error">{quoteErr}</p>}
            </div>

            {quote && (
              <div className="rounded-xl border border-cyan-400/20 bg-slate-900/40 p-3 text-sm" data-testid="quote-summary">
                <Row label="Plano" value={`${quote.plan_name} · ${quote.months === 12 ? "anual" : "mensal"}`} />
                <Row label="Preço original" value={brl(quote.original_amount)} testid="quote-original" />
                {quote.discount > 0 && <Row label={`Desconto${quote.promotion?.code ? ` (${quote.promotion.code})` : ""}`} value={`- ${brl(quote.discount)}`} accent="text-emerald-300" testid="quote-discount" />}
                <div className="my-2 h-px bg-slate-800" />
                <Row label="Total a pagar" value={brl(quote.amount)} bold testid="quote-final" />
                <p className="mt-2 text-xs text-slate-500">{quote.conditions}</p>
              </div>
            )}

            {pendingAuto && !showPix && (
              <div className="flex items-start gap-2 rounded-xl border border-slate-700 bg-slate-900/60 p-3 text-xs text-slate-300" data-testid="pending-auto-note">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" /> Status da confirmação do pagamento: <b className="ml-1">{PAYMENT_STATES[status.pending_payment.status]}</b>. Se já pagou, aguarde alguns instantes — a tela atualiza sozinha.
              </div>
            )}

            <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-3" data-testid="pix-auto-box">
              <p className="flex items-center gap-2 text-sm font-semibold text-white"><Zap className="h-4 w-4 text-cyan-300" /> Ativação automática</p>
              <p className="mt-1 text-xs text-slate-400">Pix ou cartão no checkout seguro da InfinitePay. Liberação automática assim que o pagamento for confirmado.</p>
              <Button onClick={payAuto} disabled={redirecting || !quote} className="mt-3 w-full bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="pix-auto-btn">
                {redirecting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />} Pagar {quote ? brl(quote.amount) : ""} com Pix ou cartão
              </Button>
              {autoUnavailable && <p className="mt-2 text-xs text-amber-300" data-testid="pix-auto-unavailable">{autoUnavailable}</p>}
            </div>

            {!showPix ? (
              <button type="button" onClick={openPix} disabled={!quote} className="w-full text-center text-xs text-slate-400 underline-offset-2 hover:text-cyan-300 hover:underline" data-testid="pix-manual-toggle">Prefiro Pix manual (confirmação em até 24h)</button>
            ) : loading || !pay ? (
              <div className="grid place-items-center py-6"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>
            ) : (
              <div className="space-y-3" data-testid="pix-manual-box">
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                  <div className="grid h-44 w-44 shrink-0 place-items-center rounded-xl border border-cyan-400/30 bg-white p-2"><img src={pay.qr_base64} alt="QR Code Pix" className="h-full w-full" data-testid="pix-qr-img" /></div>
                  <div className="flex-1 space-y-2 text-sm">
                    <p className="flex items-center gap-2 text-slate-200"><QrCode className="h-4 w-4 text-cyan-300" /> Escaneie no app do banco ou use o Pix copia e cola.</p>
                    <p className="text-xs text-slate-400">Favorecido: <span className="text-slate-200">Marcos Henrique Lima</span> · Valor: <span className="text-slate-200" data-testid="pix-amount">{brl(pay.amount)}</span></p>
                    <p className="text-xs text-slate-500">Identificador: <span className="font-mono-flow">{pay.txid}</span></p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                  <p className="mb-1.5 text-[11px] uppercase tracking-wider text-slate-500">Pix copia e cola</p>
                  <p className="break-all font-mono-flow text-xs text-slate-300" data-testid="pix-brcode">{pay.brcode}</p>
                  <Button size="sm" onClick={copy} variant="outline" className="mt-2 border-slate-700 bg-slate-900/50 text-cyan-300" data-testid="pix-copy-btn">{copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />} {copied ? "Copiado" : "Copiar código"}</Button>
                </div>
                {waiting ? (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="pix-waiting">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0" /> Pagamento informado. Estamos confirmando o recebimento — você será avisado aqui e no sino. Esta tela atualiza sozinha.
                  </div>
                ) : (
                  <Button onClick={reportPaid} className="w-full bg-slate-800 font-semibold text-cyan-300 hover:bg-slate-700" data-testid="pix-paid-btn"><ShieldCheck className="mr-1.5 h-4 w-4" /> Já fiz o Pix</Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, accent = "", bold = false, testid }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-slate-400">{label}</span>
      <span className={`${accent} ${bold ? "font-display text-lg font-bold text-white" : "text-slate-200"}`} data-testid={testid}>{value}</span>
    </div>
  );
}
